import { useEffect, useState } from 'react';
import BrandedPublicPage from '../pages/public/BrandedPublicPage';

interface SubdomainRouterProps {
    children: React.ReactNode;
}

/**
 * Detects if the app is running on a company subdomain and routes accordingly
 * Supports:
 * - Production: acmecorp.attendin.io → 'acmecorp'
 * - Dev: acmecorp.dev.attendin.io → 'acmecorp'
 * - QA: acmecorp.qa.attendin.io → 'acmecorp'
 * - Local testing: events.localhost → 'events'
 * 
 * Excludes:
 * - localhost (no subdomain), attendin.io, www.attendin.io
 * - dev.attendin.io, qa.attendin.io (environment URLs without company)
 */
function getSubdomain(): string | null {
    const hostname = window.location.hostname;
    
    // localhost (no subdomain) or direct attendin.io - no subdomain
    if (hostname === 'localhost' || hostname === 'attendin.io' || hostname === 'www.attendin.io') {
        return null;
    }

    const parts = hostname.split('.');
    
    // events.localhost - local testing subdomain
    if (parts.length === 2 && parts[1] === 'localhost') {
        return parts[0]; // Extract 'events' from events.localhost
    }
    
    // dev.attendin.io or qa.attendin.io - environment URLs without company subdomain
    if (parts.length === 3 && (parts[0] === 'dev' || parts[0] === 'qa')) {
        return null;
    }

    // acmecorp.dev.attendin.io or acmecorp.qa.attendin.io - company subdomain in environment
    if (parts.length === 4 && (parts[1] === 'dev' || parts[1] === 'qa')) {
        return parts[0]; // Extract 'acmecorp' from acmecorp.dev.attendin.io
    }

    // acmecorp.attendin.io - company subdomain in production
    if (parts.length === 3) {
        return parts[0];
    }

    return null;
}

export default function SubdomainRouter({ children }: SubdomainRouterProps) {
    const [subdomain] = useState<string | null>(getSubdomain());

    // If on a subdomain, show branded public page
    if (subdomain) {
        return <BrandedPublicPage />;
    }

    // Otherwise, show normal app
    return <>{children}</>;
}
